import React, { useCallback, useMemo } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { NativeResources } from '@ariksa/inventory-core/api';
import { Box } from '@chakra-ui/react';
import { findIndex } from 'lodash';
import { useSelector } from 'react-redux';

import {
  ActivityIcon,
  AppIcon,
  BellIcon,
  ComplianceIcon,
  ContainerVolumeIcon,
  DatabaseIcon,
  DataScanIcon,
  InterfaceIcon,
  IssuesIcon,
  KeyIcon,
  MonitoringIcon,
  NetworkACLIcon,
  PermissionsActionsIcon,
  PolicyIcon,
  ServicesIcon,
  TimelineIcon,
  UserIcon,
  VirtualMachineIcon,
  VulnerabilityIcon,
} from 'components/Icons';
import { AlertsTimeline } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/AletsTimeline';
import { AccessTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Access';
import { AccessPoliciesTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/AccessPolicies';
import { ActivityTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Activity';
import { AlertDetailsTable } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Alerts/AlertDetailsTable';
import { AMIHosts } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/AMIHosts/AMIHosts';
import { AppsTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Apps';
import { Compliance } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Compliance/Compliance';
import { getContainers } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Containers/getContainers';
import { CredentialsTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Credentials';
import { getDataComponent } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/getDataTab';
import { IacDeployments } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Deployment';
import { getIdentityComponent } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Identity/getIdentityTab';
import { getNetworkTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Network/getNetworkTab';
import { ResourcesByResourceGroup } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/ResourcesByResourceGroup';
import { CloudResourceOverview } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/CloudResourceOverview';
import { IdentityPermissions } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/IdentityPermissions';
import { S3Permissions } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/ObjectStorage/S3Permissions';
import { ECRImages } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/ECR/Components/ECRImages';
import { VulnerabilityTab } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { selectSharedState } from 'containers/SharedState/selectors';

import { isGoogleWorkspaceProvider } from '../Summary/utils';

export const useMetadataDrawerTabs = () => {
  const {
    resourceType,
    resourceAccountId,
    resourceId,
    resourceInsight,
    resourceDetails,
    cloudProvider,
  } = useSelector(selectActiveResource);
  const { isDevOrTest } = useSelector(selectSharedState);

  const access = useMemo(
    () => ({
      title: 'Access',
      key: MetadataTabs.Access,
      component: <AccessTab />,
      icon: <InterfaceIcon />,
    }),
    [],
  );

  const accessPolicies = useMemo(
    () => ({
      title: 'Masking Policies',
      key: MetadataTabs.AccessPolicies,
      component: <AccessPoliciesTab />,
      icon: <PolicyIcon />,
    }),
    [],
  );

  const activity = useMemo(
    () => ({
      title: 'Activity',
      key: MetadataTabs.Activity,
      icon: <ActivityIcon />,
      component: <ActivityTab />,
    }),
    [],
  );

  const summary = useMemo(
    () => ({
      title: 'Summary',
      key: MetadataTabs.Summary,
      component: <CloudResourceOverview />,
      icon: <MonitoringIcon />,
    }),
    [],
  );

  const resourcesByResourceGroup = useMemo(
    () => ({
      title: 'Resources',
      key: MetadataTabs.Resources,
      component: <ResourcesByResourceGroup />,
      icon: <ServicesIcon />,
    }),
    [],
  );

  const resourcesByStorageAccount = useMemo(
    () => ({
      title: 'Data Sources',
      key: MetadataTabs.DataSources,
      component: <ResourcesByResourceGroup />,
      icon: <DatabaseIcon />,
    }),
    [],
  );

  const alerts = useMemo(
    () => ({
      title: 'Issues',
      key: MetadataTabs.Alerts,
      component: (
        <Box w="full" h="full" flex={1}>
          <AlertDetailsTable />
        </Box>
      ),
      icon: <IssuesIcon />,
    }),
    [],
  );

  const compliance = useMemo(
    () => ({
      title: 'Compliance',
      key: MetadataTabs.Compliance,
      component: <Compliance />,
      icon: <ComplianceIcon />,
    }),
    [],
  );

  const timeline = useMemo(
    () => ({
      title: 'Timeline',
      key: MetadataTabs.Timeline,
      component: (
        <Box w="full" h="full" flex={1}>
          <AlertsTimeline />
        </Box>
      ),
      icon: <TimelineIcon />,
    }),
    [],
  );

  const vulnerabilities = useMemo(
    () => ({
      title: 'Vulnerabilities',
      key: MetadataTabs.Vulnerabilities,
      component: (
        <Box w="full" h="full" flex={1}>
          <VulnerabilityTab />
        </Box>
      ),
      icon: <VulnerabilityIcon />,
    }),
    [],
  );

  const containers = useMemo(
    () => ({
      title: 'Containers',
      key: MetadataTabs.Containers,
      component: (
        <Box w="full" h="full" flex={1}>
          {getContainers(resourceType)}
        </Box>
      ),
      icon: <ContainerVolumeIcon />,
    }),
    [resourceType],
  );

  const principals = useMemo(
    () => ({
      title: 'Principals',
      key: MetadataTabs.Principal,
      icon: <UserIcon />,
      component: getIdentityComponent(resourceType),
    }),
    [resourceType],
  );

  const secrets = useMemo(
    () => ({
      title: 'Secrets',
      key: MetadataTabs.Secrets,
      component: <CredentialsTab />,
      icon: <KeyIcon />,
    }),
    [],
  );

  const network = useMemo(
    () => ({
      title: 'Network',
      key: MetadataTabs.Network,
      component: (
        <Box w="full" h="full" flex={1}>
          {getNetworkTab(resourceType)}
        </Box>
      ),
      icon: <NetworkACLIcon />,
    }),
    [resourceType],
  );

  const identityPermissions = useMemo(
    () => ({
      title: 'Permissions',
      key: MetadataTabs.Permissions,
      component: <IdentityPermissions />,
      icon: <PermissionsActionsIcon />,
    }),
    [],
  );

  const data = useMemo(
    () => ({
      title: 'Data',
      key: MetadataTabs.Data,
      component: getDataComponent(
        resourceType,
        resourceAccountId,
        resourceType === NativeResources.DynamoDb ||
          resourceType === NativeResources.GitHubRepository ||
          resourceType === NativeResources.GitLabProject ||
          resourceType === NativeResources.BitBucketRepository
          ? resourceDetails?.data?.config?.path_with_namespace ??
              resourceInsight.data?.name ??
              resourceId
          : resourceId,
      ),
      icon: <DataScanIcon p={0.75} />,
    }),
    [
      resourceAccountId,
      resourceId,
      resourceType,
      resourceInsight.data,
      resourceDetails.data,
    ],
  );

  const deployment = useMemo(() => {
    return {
      title: 'Deployment',
      key: MetadataTabs.Deployment,
      icon: <ServicesIcon />,
      component: <IacDeployments />,
    };
  }, []);

  const containerImages = useMemo(
    () => ({
      title: 'Images',
      key: MetadataTabs.ContainerImages,
      icon: <ContainerVolumeIcon />,
      component: <ECRImages />,
    }),
    [],
  );

  const appsTab = useMemo(() => {
    return {
      title: 'Apps',
      key: MetadataTabs.Apps,
      icon: <AppIcon />,
      component: <AppsTab />,
    };
  }, []);

  const hosts = useMemo(() => {
    return {
      title: 'Hosts',
      key: MetadataTabs.Hosts,
      icon: <VirtualMachineIcon />,
      component: <AMIHosts />,
    };
  }, []);

  const lastCommonTabs = useMemo(() => [alerts, compliance, timeline], [
    alerts,
    compliance,
    timeline,
  ]);

  const getMetadataDrawerTabs = useCallback(
    type => {
      switch (type) {
        case NativeResources.Ec2Instance:
          return [
            summary,
            vulnerabilities,
            network,
            identityPermissions,
            principals,
            activity,
            secrets,
            containers,
            appsTab,
            ...lastCommonTabs,
          ];

        case NativeResources.SimpleStorageService:
          return [summary, data, principals, activity, ...lastCommonTabs];

        case NativeResources.RedshiftCluster:
        case NativeResources.DynamoDb:
        case NativeResources.RdsCluster:
        case NativeResources.DocumentDb:
        case NativeResources.DocumentDbCluster:
          return [summary, data, principals, ...lastCommonTabs];

        case NativeResources.RelationalDatabaseService:
          return [summary, data, principals, ...lastCommonTabs];

        case NativeResources.IamPolicy:
          return [summary, principals, identityPermissions, ...lastCommonTabs];

        case NativeResources.IamGroup:
          return [summary, identityPermissions, principals, ...lastCommonTabs];

        case NativeResources.SecurityGroup:
        case NativeResources.ElasticLoadBalancer:
        case NativeResources.ElasticLoadBalancerv2:
          return [summary, network, principals, ...lastCommonTabs];

        case NativeResources.AzureSecurityGroups:
          return [summary, network, ...lastCommonTabs];

        // The root account has no data component, so it gets no Data tab.
        case NativeResources.AccountRoot:
          return [summary, identityPermissions, activity, ...lastCommonTabs];

        case NativeResources.IamUser:
          return [
            summary,
            identityPermissions,
            activity,
            data,
            ...lastCommonTabs,
          ];

        case NativeResources.IamRole:
          return [
            summary,
            access,
            identityPermissions,
            activity,
            data,
            ...lastCommonTabs,
          ];

        case NativeResources.GcpUser:
        case NativeResources.GcpRole:
        case NativeResources.GcpServiceAccount:
          return [summary, identityPermissions, ...lastCommonTabs];

        case NativeResources.OktaUser:
        case NativeResources.AzureAdUser:
        case NativeResources.OktaGroup:
        case NativeResources.AzureAdGroup:
        case NativeResources.AzureAdsamlRole:
        case NativeResources.OktaRoleSubscription:
          return [summary];

        case NativeResources.SnowflakeUsers:
          return [summary, identityPermissions, data];

        case NativeResources.SnowflakeRoles:
          return [summary, identityPermissions];

        case NativeResources.OktaDomain:
        case NativeResources.OktaOrganization:
        case NativeResources.OktaApplication:
        case NativeResources.OktaIdentityProvider:
        case NativeResources.OktaPolicy:
        case NativeResources.AzureAdApplication:
        case NativeResources.AzureAdDirectoryRoles:
        case NativeResources.AzureAdOrganization:
        case NativeResources.AzureAdDomain:
        case NativeResources.IdentityCentreInstance:
          return [summary];

        case NativeResources.ElasticKubernetesService:
          return [
            summary,
            vulnerabilities,
            network,
            principals,
            identityPermissions,
            containers,
            appsTab,
            ...lastCommonTabs,
          ];

        case NativeResources.Gcpgke:
        case NativeResources.Aks:
          return [
            summary,
            vulnerabilities,
            network,
            ...(isDevOrTest ? [principals, identityPermissions] : []),
            containers,
            ...lastCommonTabs,
          ];

        case NativeResources.ElasticContainerService:
        case NativeResources.EcsCluster:
          return [
            summary,
            vulnerabilities,
            principals,
            identityPermissions,
            containers,
            ...lastCommonTabs,
          ];

        case NativeResources.ElasticContainerRegistry:
          return [
            summary,
            //vulnerabilities,
            containerImages,
            principals,
            ...lastCommonTabs,
          ];

        case NativeResources.SnowflakeDatabases:
          return [
            summary,
            data,
            //accessPolicies,
            principals,
          ];

        case NativeResources.SnowflakeTables:
        case NativeResources.SnowflakeSchemas:
          return [summary, data, principals];

        case NativeResources.Route53HostedZone:
          return [summary, network, principals, ...lastCommonTabs];

        case NativeResources.LambdaFunction:
          return [
            summary,
            vulnerabilities,
            principals,
            activity,
            ...lastCommonTabs,
          ];

        case NativeResources.AmazonMachineImage:
          return [
            summary,
            hosts,
            vulnerabilities,
            principals,
            ...lastCommonTabs,
          ];

        case NativeResources.Container:
          return [summary, vulnerabilities, network];

        case NativeResources.CloudFormation:
          return [summary, principals, compliance];

        case NativeResources.Ec2LaunchTemplate:
          return [summary, principals, compliance, timeline];

        case NativeResources.SystemsManager:
        case NativeResources.SystemsManagerDocument:
        case NativeResources.DhcpOptions:
        case NativeResources.SubnetRouteTable:
        case NativeResources.ElasticNetworkInterface:
        case NativeResources.NatGateway:
        case NativeResources.InternetGateway:
        case NativeResources.DatabaseSecurityGroup:
        case NativeResources.PrefixList:
        case NativeResources.VpcPeeringConnection:
        case NativeResources.TransitGateway:
        case NativeResources.TransitGatewayAttachment:
        case NativeResources.TransitGatewayRouteTable:
        case NativeResources.ApiGatewayDomain:
        case NativeResources.EcsContainerInstance:
        case NativeResources.VpcFlowLogs:
        case NativeResources.CloudWatchMetrics:
          return [summary, principals];

        case NativeResources.GitHubRepository:
        case NativeResources.GitLabProject:
        case NativeResources.BitBucketRepository:
          return [summary, data, deployment];

        case NativeResources.GcpComputeEngine:
        case NativeResources.GcpContainer:
        case NativeResources.AzureVirtualMachine:
        case NativeResources.AzureContainer:
        case NativeResources.AzureVirtualMachineScaleSetInstance:
        case NativeResources.AzureVirtualMachineScaleSets:
          return [summary, vulnerabilities, network, ...lastCommonTabs];

        case NativeResources.GcpCloudFunctions:
        case NativeResources.AzureFunctions:
          return [summary, vulnerabilities, ...lastCommonTabs];

        case NativeResources.AzureResourceGroup:
          return [summary, resourcesByResourceGroup, ...lastCommonTabs];

        case NativeResources.AzureStorageAccount:
          return [summary, resourcesByStorageAccount, ...lastCommonTabs];



        case NativeResources.GoogleWorkspaceGroup:
        //case NativeResources.GoogleWorkspaceRole:
          return [summary, principals, ...lastCommonTabs];

        default:
          if (
            type?.toLowerCase()?.startsWith('googleworkspace') ||
            isGoogleWorkspaceProvider(cloudProvider)
          )
            return [summary, ...lastCommonTabs];
          if (
            type?.toLowerCase()?.startsWith('gcp') ||
            cloudProvider === CloudProviders.Gcp ||
            cloudProvider === CloudProviders.Azure ||
            (type?.toLowerCase()?.startsWith('azure') &&
              !type?.toLowerCase()?.startsWith('azuread'))
          )
            return [summary, ...lastCommonTabs];
          return [summary, principals, ...lastCommonTabs];
      }
    },
    [
      summary,
      vulnerabilities,
      network,
      activity,
      principals,
      access,
      secrets,
      containers,
      lastCommonTabs,
      data,
      identityPermissions,
      containerImages,
      compliance,
      timeline,
      deployment,
      hosts,
      appsTab,
      cloudProvider,
      resourcesByResourceGroup,
      resourcesByStorageAccount,
      //accessPolicies,
      isDevOrTest,
    ],
  );

  const getTabIndex = useCallback(
    (resourceType, tabKey) => {
      const index = findIndex(
        getMetadataDrawerTabs(resourceType),
        o => o.key === tabKey,
      );
      if (index < 0) return 0;
      return index;
    },
    [getMetadataDrawerTabs],
  );

  // getTabIndex falls back to the first tab, so callers that must not navigate
  // to an unrelated tab need to check that the destination exists.
  const hasTab = useCallback(
    (resourceType, tabKey) =>
      findIndex(getMetadataDrawerTabs(resourceType), o => o.key === tabKey) >=
      0,
    [getMetadataDrawerTabs],
  );

  return {
    getMetadataDrawerTabs,
    getTabIndex,
    hasTab,
  };
};
